import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createPanelCheckout } from "../../../utils/panelPurchase";
import useStudentPortalMode from "../../../hooks/useStudentPortalMode";
import ImmediateSupplyConsentCheckbox from "./ImmediateSupplyConsentCheckbox";

/**
 * Buying something from inside the student panel, without leaving the page.
 *
 * Two steps in one dialog: confirm what is being bought and for how much, then
 * Stripe's own card form mounted in place. The student stays on the catalogue they
 * were browsing the whole way through — no jump to a Stripe-hosted page and back.
 *
 * A parent-PIN gate used to sit in front of this and was removed: parents forget
 * the PIN, and a forgotten PIN stopped the sale outright.
 */

export function useConfirmPurchase() {
  const navigate = useNavigate();
  // An ended free trial cannot buy single items (the API refuses them): send it to the plans.
  const { catalogueLocked } = useStudentPortalMode();
  const [request, setRequest] = useState(null);
  const [checkout, setCheckout] = useState(null); // { clientSecret, stripePromise }
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [parentConfirmed, setParentConfirmed] = useState(false);
  const [supplyConsent, setSupplyConsent] = useState(false);

  /**
   * @param {"mock_exam"|"paper"|"bundle"} resourceType
   * @param {number} resourceId
   * @param {{name?: string, price?: string, onCancel?: () => void}} [meta]
   */
  const requestPurchase = useCallback((resourceType, resourceId, meta = {}) => {
    if (catalogueLocked) {
      toast.info("Your free trial has ended. Choose a plan to continue.");
      navigate(`/${process.env.REACT_APP_STUDENT_PREFIX || "student"}/choose-plan?plans=1`);
      return;
    }
    setError("");
    setCheckout(null);
    setBusy(false);
    setParentConfirmed(false);
    setSupplyConsent(false);
    setRequest({ resourceType, resourceId, ...meta });
  }, [catalogueLocked, navigate]);

  // The callback runs outside the state updater: React may call an updater twice.
  const close = useCallback(() => {
    const onCancel = request?.onCancel;
    setRequest(null);
    setCheckout(null);
    setError("");
    setBusy(false);
    setParentConfirmed(false);
    setSupplyConsent(false);
    if (onCancel) onCancel();
  }, [request]);

  const submit = useCallback(async () => {
    if (!request) return;
    setBusy(true);
    setError("");
    try {
      const { clientSecret, publishableKey } = await createPanelCheckout(
        request.resourceType,
        request.resourceId,
        { immediateSupplyConsent: supplyConsent }
      );
      // loadStripe is called here, on a click, rather than at module load: the panel
      // should not fetch Stripe's script for students who never buy anything.
      setCheckout({ clientSecret, stripePromise: loadStripe(publishableKey) });
    } catch (err) {
      setError(err?.message || "Could not start payment.");
    } finally {
      setBusy(false);
    }
  }, [request, supplyConsent]);

  const paying = Boolean(checkout);

  const purchaseDialog = useMemo(
    () => (
      <Dialog
        open={Boolean(request)}
        onClose={busy ? undefined : close}
        maxWidth={paying ? "sm" : "xs"}
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {paying ? "Payment" : "Confirm purchase"}
        </DialogTitle>
        <DialogContent>
          {paying ? (
            <>
              <Box
                sx={{
                  border: "1px solid rgba(0,0,0,0.10)",
                  borderRadius: 2,
                  overflow: "hidden",
                  bgcolor: "#fff",
                }}
              >
                <EmbeddedCheckoutProvider
                  stripe={checkout.stripePromise}
                  options={{ clientSecret: checkout.clientSecret }}
                >
                  <EmbeddedCheckout />
                </EmbeddedCheckoutProvider>
              </Box>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1.5 }}>
                <LockOutlinedIcon sx={{ fontSize: 15, color: "text.secondary" }} />
                <Typography variant="caption" color="text.secondary">
                  Card details go straight to Stripe — they never reach our servers.
                </Typography>
              </Stack>
            </>
          ) : (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                You are about to buy:
              </Typography>
              <Typography sx={{ fontWeight: 700 }}>
                {request?.name || "This item"}
              </Typography>
              {request?.price ? (
                <Typography sx={{ fontWeight: 700, color: "#4c1d95", fontSize: "1.1rem" }}>
                  {request.price}
                </Typography>
              ) : null}
              <Typography variant="caption" color="text.secondary">
                Payment happens on this page, in Stripe's secure form. Nothing is charged
                until you enter a card.
              </Typography>
              <FormControlLabel
                sx={{ alignItems: "flex-start", mr: 0 }}
                control={
                  <Checkbox
                    checked={parentConfirmed}
                    onChange={(e) => setParentConfirmed(e.target.checked)}
                    sx={{ pt: 0.25 }}
                  />
                }
                label={
                  <Typography variant="body2">
                    I confirm I am the parent or guardian authorising this purchase.
                  </Typography>
                }
              />
              <ImmediateSupplyConsentCheckbox checked={supplyConsent} onChange={setSupplyConsent} />
              {error ? <Alert severity="error">{error}</Alert> : null}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={close} disabled={busy} sx={{ textTransform: "none" }}>
            {paying ? "Back" : "Cancel"}
          </Button>
          {paying ? null : (
            <Button
              variant="contained"
              onClick={submit}
              disabled={busy || !parentConfirmed || !supplyConsent}
              startIcon={<ShoppingCartIcon />}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {busy ? "Opening payment…" : "Continue to payment"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    ),
    [request, checkout, paying, error, busy, close, submit, parentConfirmed, supplyConsent]
  );

  return { requestPurchase, purchaseDialog };
}

export default useConfirmPurchase;
